import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  TraceMaterialLot,
  TraceProducedLot,
  LotForwardTrace,
  BatchBackwardTrace,
  BatchInputLot,
  TraceNodeDetail,
  TraceQaItem,
  TraceProductQa,
  FinishedProductCheck,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { openReportWindow, writeTraceabilityReport } from "./traceabilityReport";
import { writeDocumentationPack } from "./documentationPack";
import ReceiptImageThumb, { collectReceiptImages } from "./components/ReceiptImageThumb";
import { t, dateLocale } from "../../../i18n/locale";

type ViewMode = "lot-to-batch" | "batch-to-lot";
type NodeKind = "lot" | "batch" | "dispatch" | "receipt" | "transfer" | "qa" | "product_qa" | "destination";
interface SelectedNode {
  kind: NodeKind;
  id: number;
  key: string;
}

const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(dateLocale()) : "-");
const formatDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString(dateLocale(), { dateStyle: "short", timeStyle: "short" }) : "-";
const formatKg = (v?: number | null) => (v == null ? "-" : `${v.toFixed(3)} kg`);
const formatPieces = (v?: number | null) => (v == null ? "—" : `${v} ${t("kom")}`);
const formatQty = (pieces?: number | null, kg?: number | null) => {
  const parts: string[] = [];
  if (pieces != null) parts.push(`${pieces} ${t("kom")}`);
  if (kg != null) parts.push(formatKg(kg));
  return parts.length ? parts.join(" · ") : "—";
};
const isPieceItem = (name?: string | null) =>
  /kalup|vre[cć]ic|klips|traka|deklaracij|kutij|etiket|naljepnic/i.test(name || "");
const formatConsumed = (name?: string | null, v?: number | null) => {
  if (v == null || Number.isNaN(Number(v))) return "-";
  if (!isPieceItem(name)) return formatKg(v);
  const n = Number(v);
  return `${Number.isInteger(n) ? n : n.toFixed(3).replace(/\.?0+$/, "")} ${t("kom")}`;
};
const formatNum = (v?: number | null, unit = "") => (v == null ? "-" : `${v}${unit ? ` ${unit}` : ""}`);
const lotBroj = (...vals: Array<string | null | undefined>) =>
  vals.find((v) => v && !/^R-\d/.test(String(v))) || "—";
const qaSourceLabel = (source?: TraceQaItem["source"]) => {
  if (source === "qc") return t("QA odluka");
  if (source === "released") return t("pušteno");
  if (source === "reject") return t("odbijeno");
  return t("dan proizvodnje ili ranije");
};
const isInternalDocNote = (notes?: string | null) =>
  /^(ritam-import|operator-lot-link|excel-plan|s071026)\b/i.test(String(notes || "").trim());
const publicDocNote = (notes?: string | null) => (isInternalDocNote(notes) ? null : notes || null);
const formatMark = (v?: boolean | null) => (v == null ? "—" : v ? "+" : "−");
const sensoryScore = (notes: string | null | undefined, key: "miris" | "okus" | "izgled") => {
  const m = (notes || "").match(new RegExp(`${key}\\s+(\\d+(?:[.,]\\d+)?)`, "i"));
  return m ? m[1].replace(",", ".") : null;
};
const formatSensory = (ok: boolean | null | undefined, notes: string | null | undefined, key: "miris" | "okus" | "izgled") =>
  sensoryScore(notes, key) ?? formatMark(ok);
const formatPieceWeight = (kg?: number | null) => {
  if (kg == null || Number.isNaN(Number(kg))) return "—";
  const n = Number(kg);
  if (n > 0 && n < 2) return `${Math.round(n * 1000)} g`;
  return `${n} kg`;
};
const productQaCaption = (pq?: TraceProductQa | null) => {
  if (!pq?.recorded) return t("nema analize");
  if (pq.all_passed === false) return t("ne zadovoljava");
  return t("zadovoljava");
};

// ---------------------------------------------------------------- node card
const NodeCard: React.FC<{
  variant: "input" | "batch" | "customer" | "semi" | "receipt" | "transfer" | "qa" | "product_qa" | "destination";
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  kicker: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  meta?: React.ReactNode;
  flags?: React.ReactNode;
}> = ({ variant, active, disabled, onClick, kicker, title, sub, meta, flags }) => (
  <button
    type="button"
    className={`tnode is-${variant}${active ? " active" : ""}`}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="tnode-kicker">{kicker}</span>
    <span className="tnode-title">{title}</span>
    <span className={`tnode-sub${sub ? "" : " is-blank"}`}>{sub || "\u00a0"}</span>
    {flags ? <span className="tnode-flags">{flags}</span> : null}
    <span className={`tnode-meta${meta ? "" : " is-blank"}`}>{meta || "\u00a0"}</span>
  </button>
);

const DRow: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <div className="trace-d-row">
    <span>{label}</span>
    <strong>{children}</strong>
  </div>
);

const StatusPill: React.FC<{ value?: string | null }> = ({ value }) =>
  value ? <span className={`trace-status s-${String(value).toLowerCase()}`}>{value}</span> : <>-</>;

const Traceability = () => {
  savePathToLocalStorage();

  const [viewMode, setViewMode] = useState<ViewMode>("lot-to-batch");
  const [lots, setLots] = useState<TraceMaterialLot[]>([]);
  const [producedLots, setProducedLots] = useState<TraceProducedLot[]>([]);
  const [selectedLotId, setSelectedLotId] = useState<number>(0);
  const [selectedBatchId, setSelectedBatchId] = useState<number>(0);
  const [forward, setForward] = useState<LotForwardTrace | null>(null);
  const [backward, setBackward] = useState<BatchBackwardTrace | null>(null);
  const [loading, setLoading] = useState(false);
  // Uvezeni utrošak zna sadržavati sirovine bez ulaznog dokumenta (zaliha
  // otvorena prije početka uvoza), pa ih se može sakriti iz dijagrama.
  const [onlyWithReceipt, setOnlyWithReceipt] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [docsExporting, setDocsExporting] = useState(false);

  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [nodeDetail, setNodeDetail] = useState<TraceNodeDetail | null>(null);
  const [nodeLoading, setNodeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const sidebarRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    loadLots();
    loadProducedLots();
  }, [includeHidden]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!selectedNode) return;
    const node = document.querySelector(".tnode.active") as HTMLElement | null;
    node?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    sidebarRef.current?.classList.remove("flash");
    void sidebarRef.current?.offsetWidth;
    sidebarRef.current?.classList.add("flash");
  }, [selectedNode]);

  const loadLots = async () => {
    try {
      setLots(await mesApi.traceMaterialLots());
    } catch (error: any) {
      toast.error(t("Greška kod učitavanja lotova: {message}", { message: error.message }));
    }
  };

  const loadProducedLots = async () => {
    try {
      setProducedLots(await mesApi.traceProducedLots({ include_hidden: includeHidden }));
    } catch (error: any) {
      toast.error(t("Greška kod učitavanja proizvedenih lotova: {message}", { message: error.message }));
    }
  };

  const exportReport = async () => {
    if (viewMode !== "batch-to-lot" || !selectedBatchId) {
      toast.info(t("Odaberi proizvedeni lot za izvoz izvještaja."));
      return;
    }
    let win: Window | null = null;
    try {
      setExporting(true);
      win = openReportWindow();
      const report = await mesApi.traceBatchReport(selectedBatchId);
      writeTraceabilityReport(win, report);
    } catch (error: any) {
      win?.close();
      toast.error(t("Izvoz izvještaja nije uspio: {message}", { message: error.message }));
    } finally {
      setExporting(false);
    }
  };

  const exportDocumentation = async () => {
    if (viewMode !== "batch-to-lot" || !selectedBatchId) {
      toast.info(t("Odaberi proizvedeni lot za preuzimanje dokumentacije."));
      return;
    }
    let win: Window | null = null;
    try {
      setDocsExporting(true);
      win = openReportWindow();
      const pack = await mesApi.traceBatchDocumentation(selectedBatchId);
      writeDocumentationPack(win, pack);
    } catch (error: any) {
      win?.close();
      toast.error(t("Preuzimanje dokumentacije nije uspjelo: {message}", { message: error.message }));
    } finally {
      setDocsExporting(false);
    }
  };

  const resetNode = () => {
    setSelectedNode(null);
    setNodeDetail(null);
  };

  const handleLotSelect = async (lotId: number) => {
    setSelectedLotId(lotId);
    setForward(null);
    resetNode();
    setSearchOpen(false);
    if (!lotId) return;
    try {
      setLoading(true);
      setForward(await mesApi.traceLotForward(lotId));
    } catch (error: any) {
      toast.error(t("Greška: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSelect = async (batchId: number) => {
    setSelectedBatchId(batchId);
    setBackward(null);
    resetNode();
    setSearchOpen(false);
    if (!batchId) return;
    try {
      setLoading(true);
      setBackward(await mesApi.traceBatchBackward(batchId));
    } catch (error: any) {
      toast.error(t("Greška: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (mode: ViewMode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    setSearch("");
    resetNode();
  };

  const openNode = async (kind: NodeKind, id?: number | null) => {
    if (kind !== "qa" && !id) return;
    const key = `${kind}-${id ?? 0}`;
    setSelectedNode({ kind, id: id ?? 0, key });
    setNodeDetail(null);
    setActiveTab(0);
    try {
      setNodeLoading(true);
      if (kind === "qa") {
        setNodeDetail({
          kind: "qa",
          batch_id: backward?.batch.batch_id ?? id ?? 0,
          production_date: backward?.batch.production_date ?? null,
          all_passed: backward?.qa?.all_passed !== false,
          items: backward?.qa?.items ?? [],
        });
        return;
      }
      if (kind === "product_qa") {
        const pq = backward?.product_qa;
        setNodeDetail({
          kind: "product_qa",
          batch_id: backward?.batch.batch_id ?? id ?? 0,
          product_name: backward?.batch.product_name ?? null,
          production_lot_code: backward?.batch.production_lot_code ?? null,
          mix_code: backward?.batch.mix_code ?? null,
          production_date: backward?.batch.production_date ?? null,
          all_passed: pq?.all_passed !== false,
          recorded: !!pq?.recorded,
          latest: pq?.latest ?? null,
          checks: pq?.checks ?? [],
        });
        return;
      }
      if (kind === "destination") {
        const dest =
          (backward?.destinations ?? []).find(
            (d) => d.warehouse_id === id || d.transfer_id === id
          ) ?? { warehouse_id: id };
        const transfer =
          (backward?.transfers_out ?? []).find(
            (t) => t.id === dest.transfer_id || t.to_warehouse_id === dest.warehouse_id
          ) ?? null;
        setNodeDetail({
          kind: "destination",
          destination: dest,
          transfer,
          dispatches: (backward?.dispatches ?? []).filter((d) => d.warehouse_id === dest.warehouse_id),
        });
        return;
      }
      let d: TraceNodeDetail;
      if (kind === "lot") d = await mesApi.traceNodeLot(id!);
      else if (kind === "batch") d = await mesApi.traceNodeBatch(id!);
      else if (kind === "receipt") d = await mesApi.traceNodeReceipt(id!);
      else if (kind === "transfer") d = await mesApi.traceNodeTransfer(id!);
      else d = await mesApi.traceNodeDispatch(id!);
      setNodeDetail(d);
    } catch (error: any) {
      toast.error(t("Greška kod učitavanja detalja: {message}", { message: error.message }));
    } finally {
      setNodeLoading(false);
    }
  };

  const isActive = (kind: NodeKind, id?: number | null) =>
    !!id && selectedNode?.key === `${kind}-${id}`;

  // -------------------------------------------------------------- search list
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (viewMode === "lot-to-batch") {
      return lots
        .filter(
          (l) =>
            !q ||
            (l.supplier_lot || "").toLowerCase().includes(q) ||
            (l.internal_lot_code || "").toLowerCase().includes(q) ||
            (l.ingredient_name || "").toLowerCase().includes(q) ||
            (l.supplier_name || "").toLowerCase().includes(q)
        )
        .slice(0, 40);
    }
    return producedLots
      .filter(
        (b) =>
          !q ||
          (b.production_lot_code || "").toLowerCase().includes(q) ||
          (b.mix_code || "").toLowerCase().includes(q) ||
          (b.product_name || "").toLowerCase().includes(q)
      )
      .slice(0, 40);
  }, [search, viewMode, lots, producedLots]);

  const selectedLabel = useMemo(() => {
    if (viewMode === "lot-to-batch") {
      const l = lots.find((x) => x.lot_id === selectedLotId);
      return l ? `${lotBroj(l.supplier_lot, l.internal_lot_code) !== "—" ? lotBroj(l.supplier_lot, l.internal_lot_code) : `#${l.lot_id}`} · ${l.ingredient_name || "-"}` : "";
    }
    const b = producedLots.find((x) => x.batch_id === selectedBatchId);
    return b ? `${b.production_lot_code || b.mix_code} · ${b.product_name || "-"}` : "";
  }, [viewMode, lots, producedLots, selectedLotId, selectedBatchId]);

  const hasSelection = viewMode === "lot-to-batch" ? !!selectedLotId : !!selectedBatchId;

  // -------------------------------------------------------------- diagram
  const renderDiagram = () => {
    if (loading) return <div className="trace-empty">{t("Učitavanje…")}</div>;
    if (!hasSelection) {
      return (
        <div className="trace-empty">
          {viewMode === "lot-to-batch"
            ? t("Pretraži i odaberi ulazni lot da vidiš dijagram sljedivosti")
            : t("Pretraži i odaberi proizvedeni lot da vidiš dijagram sljedivosti")}
        </div>
      );
    }

    if (viewMode === "lot-to-batch") {
      if (!forward) return <div className="trace-empty">{t("Nema podataka")}</div>;
      const cons = forward.consumptions;
      const semiCons = cons.filter((c) => c.is_semi_finished);
      const directCons = cons.filter((c) => !c.is_semi_finished);

      // Group by produced lot, not by batch. A lot code can cover a whole
      // production day across many products, so one input lot may reach a few
      // hundred batches but only ~20 lots — the lot is the unit a recall acts
      // on, so that is what the diagram shows.
      //
      // This also merges convergence: the same batch can be reached through
      // several semi-finished products (the same flour feeding both the starter
      // and the enzyme of one loaf), and must still appear once.
      type FinalNode = {
        batch_id: number;
        lot: string;
        products: string[];
        batchCount: number;
        kg: number | null | undefined;
        date: string | null | undefined;
        dispatches: (typeof cons)[number]["dispatches"];
        viaSemi: string[];
      };
      const finalMap = new Map<string, FinalNode>();
      const seenBatches = new Map<string, Set<number>>();
      const addFinal = (
        b: {
          batch_id: number;
          production_lot_code?: string | null;
          mix_code?: string | null;
          product_name?: string | null;
          planned_kg?: number | null;
          actual_kg?: number | null;
          production_date?: string | null;
          dispatches: (typeof cons)[number]["dispatches"];
        },
        via: string | null
      ) => {
        const key = b.production_lot_code || `#${b.batch_id}`;
        const cur =
          finalMap.get(key) ??
          ({
            batch_id: b.batch_id,
            lot: b.production_lot_code || b.mix_code || `#${b.batch_id}`,
            products: [],
            batchCount: 0,
            kg: null,
            date: b.production_date,
            dispatches: [],
            viaSemi: [],
          } as FinalNode);

        const seen = seenBatches.get(key) ?? new Set<number>();
        if (!seen.has(b.batch_id)) {
          seen.add(b.batch_id);
          cur.batchCount += 1;
          const mass = b.actual_kg ?? b.planned_kg;
          if (mass != null) cur.kg = (cur.kg ?? 0) + mass;
          if (b.product_name && !cur.products.includes(b.product_name)) cur.products.push(b.product_name);
        }
        seenBatches.set(key, seen);

        for (const d of b.dispatches || []) {
          const dup = d.dispatch_id != null && cur.dispatches.some((x) => x.dispatch_id === d.dispatch_id);
          if (!dup) cur.dispatches.push(d);
        }
        if (via && !cur.viaSemi.includes(via)) cur.viaSemi.push(via);
        finalMap.set(key, cur);
      };
      directCons.forEach((c) => addFinal(c, null));
      semiCons.forEach((s) =>
        (s.downstream || []).forEach((ds) =>
          addFinal(ds, s.product_name || s.production_lot_code || `#${s.batch_id}`)
        )
      );
      const finals = Array.from(finalMap.values());

      const customerLeaf = (dispatches: (typeof cons)[number]["dispatches"]) =>
        dispatches.length === 0 ? (
          <div className="trace-leaf-empty">{t("nije otpremljeno")}</div>
        ) : (
          dispatches.map((d, k) => (
            <NodeCard
              key={k}
              variant="customer"
              disabled={!d.dispatch_id}
              active={isActive("dispatch", d.dispatch_id)}
              onClick={() => openNode("dispatch", d.dispatch_id)}
              kicker={t("Kupac")}
              title={d.customer_name || t("Nepoznat kupac")}
              meta={`${d.dispatch_number || ""}${d.dispatch_date ? ` · ${formatDate(d.dispatch_date)}` : ""}`}
            />
          ))
        );

      return (
        <div className="trace-flow forward">
          <div className="flow-col col-source">
            <div className="flow-col-label">{t("Ulazni lot")}</div>
            <NodeCard
              variant="input"
              active={isActive("lot", forward.lot.lot_id)}
              onClick={() => openNode("lot", forward.lot.lot_id)}
              kicker={t("Sirovina")}
              title={lotBroj(forward.lot.supplier_lot, forward.lot.internal_lot_code)}
              sub={forward.lot.ingredient_name || "-"}
              meta={
                <>
                  {forward.lot.supplier_name || ""}
                  {forward.lot.quantity_kg != null ? ` · ${formatKg(forward.lot.quantity_kg)}` : ""}
                </>
              }
            />
          </div>

          {cons.length === 0 ? (
            <>
              <div className="flow-conn" aria-hidden />
              <div className="flow-col col-rows">
                <div className="trace-empty small">{t("Lot još nije iskorišten u proizvodnji")}</div>
              </div>
            </>
          ) : (
            <>
              {semiCons.length > 0 && (
                <>
                  <div className="flow-conn" aria-hidden />
                  <div className="flow-col col-rows">
                    <div className="flow-col-label">{t("Polu-proizvodi ({count})", { count: semiCons.length })}</div>
                    {semiCons.map((s, i) => (
                      <NodeCard
                        key={i}
                        variant="semi"
                        active={isActive("batch", s.batch_id)}
                        onClick={() => openNode("batch", s.batch_id)}
                        kicker={t("Polu-proizvod")}
                        title={s.product_name || s.production_lot_code || s.mix_code || `#${s.batch_id}`}
                        sub={s.production_lot_code || s.mix_code || "-"}
                        meta={`${formatKg(s.actual_kg ?? s.planned_kg)} · ${formatDate(s.production_date)}`}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className="flow-conn" aria-hidden />
              <div className="flow-col col-rows">
                <div className="flow-col-label">{t("Proizvedeni lot ({count})", { count: finals.length })}</div>
                {finals.length === 0 ? (
                  <div className="trace-empty small">
                    {semiCons.length > 0
                      ? t("Polu-proizvod još nije ugrađen u gotovi proizvod")
                      : t("Nema proizvedenih lotova")}
                  </div>
                ) : (
                  finals.map((f, i) => (
                    <div className="flow-row" key={i}>
                      <NodeCard
                        variant="batch"
                        active={isActive("batch", f.batch_id)}
                        onClick={() => openNode("batch", f.batch_id)}
                        kicker={f.batchCount > 1 ? t("Proizvedeni lot · {count} zamjesa", { count: f.batchCount }) : t("Glavni proizvod")}
                        title={f.lot}
                        sub={
                          f.products.length <= 2
                            ? f.products.join(", ") || "-"
                            : t("{name} i još {count}", { name: f.products[0], count: f.products.length - 1 })
                        }
                        meta={
                          <>
                            {formatKg(f.kg)} · {formatDate(f.date)}
                            {f.viaSemi.length > 0 && (
                              <span className="tnode-via">{t("preko: {names}", { names: f.viaSemi.join(", ") })}</span>
                            )}
                          </>
                        }
                      />
                      <div className="flow-conn small" aria-hidden />
                      <div className="flow-leaf">{customerLeaf(f.dispatches)}</div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    // batch-to-lot
    if (!backward) return <div className="trace-empty">{t("Nema podataka")}</div>;

    const rawInputCard = (inp: BatchInputLot, key: React.Key) => (
      <NodeCard
        key={key}
        variant="input"
        active={isActive("lot", inp.lot_id)}
        disabled={!inp.lot_id}
        onClick={() => inp.lot_id && openNode("lot", inp.lot_id)}
      kicker={inp.ingredient_name || t("Sirovina")}
      title={lotBroj(inp.supplier_lot, inp.internal_lot_code)}
      sub={inp.supplier_name || ""}
      flags={
        <>
          {inp.has_receipt === false && !inp.is_semi_finished ? <em className="tflag warn">{t("bez primke")}</em> : null}
          {inp.is_pooled ? <em className="tflag">{t("utrošak naloga")}</em> : null}
        </>
      }
      meta={formatConsumed(inp.ingredient_name, inp.actual_kg ?? inp.planned_kg)}
      />
    );

    const shown = onlyWithReceipt
      ? backward.inputs.filter((i) => i.has_receipt !== false || (i.is_semi_finished && i.produced_by))
      : backward.inputs;
    const hiddenCount = backward.inputs.length - shown.length;
    const semiInputs = shown.filter((i) => i.is_semi_finished && i.produced_by);
    const directInputs = shown.filter((i) => !(i.is_semi_finished && i.produced_by));
    const receipts = backward.receipts ?? [];
    const transfersIn = backward.transfers_in ?? [];
    const transfersOut = backward.transfers_out ?? [];
    const destinations = backward.destinations ?? [];
    const qa = backward.qa;
    const productQa = backward.product_qa;

    return (
      <div className="trace-flow backward">
        <div className="flow-col col-docs">
          <div className="flow-col-label">{t("Primke ({count})", { count: receipts.length })}</div>
          {receipts.length === 0 ? (
            <div className="trace-empty small">{t("Nema primki na utrošenim lotovima")}</div>
          ) : (
            receipts.map((r) => (
              <NodeCard
                key={r.id}
                variant="receipt"
                active={isActive("receipt", r.id)}
                onClick={() => openNode("receipt", r.id)}
                kicker={t("Primka")}
                title={r.number || `#${r.id}`}
                sub={r.supplier_name || ""}
                meta={`${r.warehouse || ""}${r.date ? ` · ${formatDate(r.date)}` : ""}`}
              />
            ))
          )}
        </div>

        <div className="flow-conn" aria-hidden />

        <div className="flow-col col-qa">
          <div className="flow-col-label">{t("QA")}</div>
          <NodeCard
            variant="qa"
            active={isActive("qa", backward.batch.batch_id)}
            onClick={() => openNode("qa", backward.batch.batch_id)}
            kicker={t("Kontrola kvalitete")}
            title={t("QA")}
            sub={
              qa?.all_passed === false
                ? t("Ima odbijenih sirovina")
                : qa?.items?.length
                  ? t("{count} sirovina", { count: qa.items.length })
                  : shown.length
                    ? t("nema evidentiranih lotova")
                    : t("nema sirovina")
            }
            meta={
              qa?.items?.length
                ? t("odobreno {date} ili ranije", {
                    date: formatDate(
                      qa.items.reduce(
                        (latest, item) =>
                          !latest || (item.approved_at && item.approved_at > latest) ? item.approved_at : latest,
                        null as string | null
                      )
                    ),
                  })
                : formatDate(backward.batch.production_date)
            }
          />
        </div>

        <div className="flow-conn" aria-hidden />

        <div className="flow-col col-docs">
          <div className="flow-col-label">{t("Međuskladišnice ({count})", { count: transfersIn.length })}</div>
          {transfersIn.length === 0 ? (
            <div className="trace-empty small">{t("Nema premještaja ZGSM → ZGPM")}</div>
          ) : (
            transfersIn.map((xfer) => (
              <NodeCard
                key={xfer.id}
                variant="transfer"
                active={isActive("transfer", xfer.id)}
                onClick={() => openNode("transfer", xfer.id)}
                kicker={t("Pekara materijalno")}
                title={xfer.number || `#${xfer.id}`}
                sub={`${xfer.from_code || xfer.from_warehouse || "?"} → ${xfer.to_code || xfer.to_warehouse || "?"}`}
                meta={xfer.date ? formatDate(xfer.date) : ""}
              />
            ))
          )}
        </div>

        <div className="flow-conn" aria-hidden />

        <div className="flow-col col-inputs">
          <div className="flow-col-label">
            {t("Ulazne sirovine")}
            {hiddenCount > 0 ? <span className="tflag warn">{t("skriveno {count}", { count: hiddenCount })}</span> : null}
          </div>
          {shown.length === 0 ? (
            <div className="trace-empty small">
              {backward.inputs.length === 0
                ? t("Nema evidencije ulaznih lotova")
                : t("Nijedna ulazna sirovina nema primku")}
            </div>
          ) : (
            <>
              {directInputs.length > 0 && (
                <div className="bw-group">
                  <div className="bw-group-body">
                    <div className="bw-raws">{directInputs.map((inp, i) => rawInputCard(inp, i))}</div>
                  </div>
                </div>
              )}
              {semiInputs.map((inp, gi) => (
                <div className="bw-group is-semi" key={`sf-${gi}`}>
                  <span className="bw-group-tag">
                    {t("za polu-proizvod: {name}", { name: inp.produced_by!.product_name || inp.ingredient_name })}
                    {inp.produced_by!.inferred
                      ? ` · ${t("iz recepture")}`
                      : (inp.produced_by!.batch_count ?? 1) > 1
                        ? ` · ${t("{count} zamjesa u istom lotu", { count: inp.produced_by!.batch_count })}`
                        : ""}
                  </span>
                  <div className="bw-group-body">
                    <div className="bw-raws">
                      {inp.produced_by!.inputs.length === 0 ? (
                        <div className="trace-empty small">{t("nema evidentiranih sirovina")}</div>
                      ) : (
                        inp.produced_by!.inputs.map((sub, k) =>
                          sub.is_semi_finished ? (
                            <NodeCard
                              key={`sf-in-${k}`}
                              variant="semi"
                              active={isActive("batch", sub.produced_by?.batch_id)}
                              disabled={!sub.produced_by?.batch_id}
                              onClick={() =>
                                sub.produced_by?.batch_id && openNode("batch", sub.produced_by.batch_id)
                              }
                              kicker={t("Polu-proizvod")}
                              title={sub.produced_by?.product_name || sub.ingredient_name || t("Polu-proizvod")}
                              sub={lotBroj(
                                sub.produced_by?.production_lot_code,
                                sub.supplier_lot,
                                sub.internal_lot_code
                              )}
                              meta={`${formatConsumed(sub.ingredient_name, sub.actual_kg ?? sub.planned_kg)} · ${formatDate(sub.produced_by?.production_date)}`}
                            />
                          ) : (
                            rawInputCard(sub, k)
                          )
                        )
                      )}
                    </div>
                    <div className="flow-conn small" aria-hidden />
                    <NodeCard
                      variant="semi"
                      active={isActive("batch", inp.produced_by!.batch_id)}
                      disabled={!inp.produced_by!.batch_id}
                      onClick={() => inp.produced_by!.batch_id && openNode("batch", inp.produced_by!.batch_id)}
                      kicker={inp.produced_by!.inferred ? t("Polu-proizvod · iz recepture") : t("Polu-proizvod")}
                      title={inp.produced_by!.product_name || inp.ingredient_name || t("Polu-proizvod")}
                      sub={
                        inp.produced_by!.inferred
                          ? t("nije proizveden kao nalog")
                          : inp.produced_by!.production_lot_code || inp.internal_lot_code || "-"
                      }
                      meta={`${formatConsumed(inp.ingredient_name, inp.actual_kg ?? inp.planned_kg)} · ${formatDate(inp.produced_by!.production_date)}`}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="flow-conn" aria-hidden />

        <div className="flow-col col-center">
          <div className="flow-col-label">{t("Proizvedeni lot")}</div>
          <NodeCard
            variant="batch"
            active={isActive("batch", backward.batch.batch_id)}
            onClick={() => openNode("batch", backward.batch.batch_id)}
            kicker={t("Zamjes")}
            title={backward.batch.production_lot_code || backward.batch.mix_code || `#${backward.batch.batch_id}`}
            sub={backward.batch.product_name || "-"}
            meta={`${formatQty(backward.batch.packed_pieces, backward.batch.final_mass_kg)}${
              backward.batch.scrap_pieces != null ? ` · ${t("{count} kom otpis", { count: backward.batch.scrap_pieces })}` : ""
            } · ${formatDate(backward.batch.production_date)}`}
          />
        </div>

        <div className="flow-conn" aria-hidden />

        <div className="flow-col col-qa">
          <div className="flow-col-label">{t("QA")}</div>
          <NodeCard
            variant="product_qa"
            active={isActive("product_qa", backward.batch.batch_id)}
            onClick={() => openNode("product_qa", backward.batch.batch_id)}
            kicker={t("Analiza gotovog proizvoda")}
            title={t("QA")}
            sub={productQaCaption(productQa)}
            meta={
              productQa?.latest?.control_date
                ? formatDate(productQa.latest.control_date)
                : formatDate(backward.batch.production_date)
            }
          />
        </div>

        {(transfersOut.length > 0 || destinations.length > 0) && (
          <>
            <div className="flow-conn" aria-hidden />
            <div className="flow-col col-dest">
              <div className="flow-col-label">{t("Odredište ({count})", { count: transfersOut.length + destinations.length })}</div>
              {transfersOut.map((xfer) => (
                <NodeCard
                  key={`out-${xfer.id}`}
                  variant="transfer"
                  active={isActive("transfer", xfer.id)}
                  onClick={() => openNode("transfer", xfer.id)}
                  kicker={xfer.purpose === "writeoff" ? t("Otpis / škart") : t("Izlaz gotovog")}
                  title={xfer.number || `#${xfer.id}`}
                  sub={`${xfer.from_code || xfer.from_warehouse || "?"} → ${xfer.to_code || xfer.to_warehouse || "?"}`}
                  meta={`${formatQty(xfer.quantity_pieces, xfer.quantity_kg)}${xfer.date ? ` · ${formatDate(xfer.date)}` : ""}`}
                />
              ))}
              {destinations
                .filter((d) => !transfersOut.some((t) => t.id === d.transfer_id || t.to_warehouse_id === d.warehouse_id))
                .map((d, i) => (
                  <NodeCard
                    key={`dest-${d.warehouse_id ?? i}`}
                    variant="destination"
                    active={isActive("destination", d.warehouse_id ?? d.transfer_id)}
                    onClick={() => openNode("destination", d.warehouse_id ?? d.transfer_id)}
                    kicker={d.kind === "wholesale" ? t("Veleprodaja") : d.kind === "production" ? t("Proizvodnja") : t("Skladište")}
                    title={d.name || d.code || t("Odredište")}
                    sub={d.code || ""}
                  />
                ))}
            </div>
          </>
        )}

        {backward.dispatches.length > 0 && (
          <>
            <div className="flow-conn" aria-hidden />
            <div className="flow-col col-customers">
              <div className="flow-col-label">{t("Kupci ({count})", { count: backward.dispatches.length })}</div>
              {backward.dispatches.map((d, i) => (
                <NodeCard
                  key={i}
                  variant="customer"
                  disabled={!d.dispatch_id}
                  active={isActive("dispatch", d.dispatch_id)}
                  onClick={() => openNode("dispatch", d.dispatch_id)}
                  kicker={t("Kupac")}
                  title={d.customer_name || t("Nepoznat kupac")}
                  sub={d.dispatch_number || ""}
                  meta={`${d.dispatch_date ? formatDate(d.dispatch_date) : ""}${d.quantity != null ? ` · ${d.quantity} ${t("kom/kg")}` : ""}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------- sidebar
  const renderSidebar = () => {
    if (!selectedNode) {
      return (
        <div className="trace-side-empty">
          <div className="tse-icon">🧭</div>
          <p>{t("Klikni na bilo koji blok u dijagramu da vidiš detalje.")}</p>
          <span>{t("Ulazni lotovi, zamjesi, recepti, radni nalozi i otpremnice.")}</span>
        </div>
      );
    }
    if (nodeLoading || !nodeDetail) return <div className="trace-empty">{t("Učitavanje detalja…")}</div>;

    if (nodeDetail.kind === "lot") return renderLotDetail(nodeDetail);
    if (nodeDetail.kind === "batch") return renderBatchDetail(nodeDetail);
    if (nodeDetail.kind === "receipt") return renderReceiptDetail(nodeDetail);
    if (nodeDetail.kind === "transfer") return renderTransferDetail(nodeDetail);
    if (nodeDetail.kind === "qa") return renderQaDetail(nodeDetail);
    if (nodeDetail.kind === "product_qa") return renderProductQaDetail(nodeDetail);
    if (nodeDetail.kind === "destination") return renderDestinationDetail(nodeDetail);
    return renderDispatchDetail(nodeDetail);
  };

  const TabBar: React.FC<{ tabs: string[] }> = ({ tabs }) => (
    <div className="trace-tabs">
      {tabs.map((tab, i) => (
        <button key={i} className={activeTab === i ? "active" : ""} onClick={() => setActiveTab(i)}>
          {tab}
        </button>
      ))}
    </div>
  );

  const renderLotDetail = (d: Extract<TraceNodeDetail, { kind: "lot" }>) => {
    const transfers = d.transfers ?? [];
    const tabs = [
      t("Pregled"),
      t("Primke ({count})", { count: d.receipts.length }),
      t("Međuskladišnice ({count})", { count: transfers.length }),
      t("Korišten u ({count})", { count: d.used_in.length }),
    ];
    return (
      <div className="trace-side">
        <div className="trace-side-head is-input">
          <span className="tsh-kicker">{t("Ulazni lot")}</span>
          <h3>{lotBroj(d.lot.supplier_lot, d.lot.internal_lot_code) !== "—" ? lotBroj(d.lot.supplier_lot, d.lot.internal_lot_code) : `#${d.lot.lot_id}`}</h3>
          <span className="tsh-sub">{d.lot.ingredient_name || "-"}</span>
        </div>
        <TabBar tabs={tabs} />
        <div className="trace-side-body">
          {activeTab === 0 && (
            <div className="trace-d-list">
              <DRow label={t("Sirovina")}>{d.lot.ingredient_name || "-"}</DRow>
              <DRow label={t("LOT broj")}>{lotBroj(d.lot.supplier_lot, d.lot.internal_lot_code)}</DRow>
              <DRow label={t("Dobavljač")}>{d.lot.supplier_name || "-"}</DRow>
              <DRow label={t("Status")}><StatusPill value={d.lot.status} /></DRow>
              <DRow label={t("Primljeno")}>{formatDate(d.lot.arrival_date)}</DRow>
              <DRow label={t("Rok trajanja")}>{formatDate(d.lot.expiry_date || d.receipts.find((r) => r.expiry_date)?.expiry_date)}</DRow>
              <DRow label={t("Utrošena količina")}>
                {formatConsumed(
                  d.lot.ingredient_name,
                  (d.used_in || []).length
                    ? (d.used_in || []).reduce((sum, u) => {
                        const v = u.actual_kg ?? u.planned_kg;
                        return v == null ? sum : sum + Number(v);
                      }, 0)
                    : null,
                )}
              </DRow>
            </div>
          )}
          {activeTab === 1 &&
            (d.receipts.length === 0 ? (
              <div className="trace-empty small">{t("Nema povezanih primki")}</div>
            ) : (
              d.receipts.map((r) => (
                <div className="trace-doc-block" key={r.line_id}>
                  <div className="tdb-head">
                    <span className="tdb-icon">📦</span>
                    <strong>{t("Primka {number}", { number: r.receipt?.number || `#${r.receipt?.id ?? ""}` })}</strong>
                    {collectReceiptImages(r.receipt).length ? (
                      <ReceiptImageThumb images={collectReceiptImages(r.receipt)} />
                    ) : null}
                  </div>
                  <DRow label={t("Datum primke")}>{formatDate(r.receipt?.date)}</DRow>
                  <DRow label={t("Dobavljač")}>{r.receipt?.supplier_name || r.purchase_order?.supplier_name || "-"}</DRow>
                  {r.price_per_kg != null ? <DRow label={t("Cijena/kg")}>{r.price_per_kg.toFixed(2)} €</DRow> : null}
                  {r.purchase_order ? (
                    <div className="tdb-sub">
                      <span className="tdb-icon">🧾</span>
                      {t("Narudžba")} <strong>{r.purchase_order.number || `#${r.purchase_order.id}`}</strong>
                      {" · "}
                      {formatDate(r.purchase_order.date)}
                      {r.purchase_order.status ? <> · <StatusPill value={r.purchase_order.status} /></> : null}
                    </div>
                  ) : (
                    <div className="tdb-sub muted">{t("Bez povezane narudžbe")}</div>
                  )}
                </div>
              ))
            ))}
          {activeTab === 2 &&
            (transfers.length === 0 ? (
              <div className="trace-empty small">{t("Lot nije premještan među skladištima")}</div>
            ) : (
              transfers.map((xfer) => (
                <div className="trace-doc-block" key={xfer.line_id}>
                  <div className="tdb-head">
                    <span className="tdb-icon">🔁</span>
                    <strong>{t("Međuskladišnica {number}", { number: xfer.number || `#${xfer.transfer_id ?? ""}` })}</strong>
                  </div>
                  <DRow label={t("Datum")}>{formatDate(xfer.date)}</DRow>
                  <DRow label={t("Iz skladišta")}>{xfer.from_warehouse || "-"}</DRow>
                  <DRow label={t("U skladište")}>{xfer.to_warehouse || "-"}</DRow>
                  <DRow label={t("Količina")}>{formatKg(xfer.quantity_kg)}</DRow>
                </div>
              ))
            ))}
          {activeTab === 3 &&
            (d.used_in.length === 0 ? (
              <div className="trace-empty small">{t("Lot još nije iskorišten")}</div>
            ) : (
              <div className="mes-table-wrap">
                <table className="trace-mini-table">
                <thead>
                  <tr>
                    <th>{t("Proizvedeni lot / zamjes")}</th>
                    <th>{t("Proizvod")}</th>
                    <th>{t("Datum")}</th>
                    <th style={{ textAlign: "right" }}>{t("Utrošeno")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.used_in.map((u, i) => (
                    <tr key={i} className="clickable" onClick={() => openNode("batch", u.batch_id)}>
                      <td><strong>{u.production_lot_code || u.mix_code || `#${u.batch_id}`}</strong></td>
                      <td>{u.product_name || "-"}</td>
                      <td>{formatDate(u.production_date)}</td>
                      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                        {formatKg(u.actual_kg ?? u.planned_kg)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderBatchDetail = (d: Extract<TraceNodeDetail, { kind: "batch" }>) => {
    const tabs = [
      t("Pregled"),
      t("Radni nalog"),
      d.recipe ? t("Recept ({count})", { count: d.recipe.ingredients.length }) : t("Recept"),
      t("Sastav zamjesa ({count})", { count: d.inputs.length }),
      t("Otprema ({count})", { count: d.dispatches.length }),
    ];
    const p = d.process;
    return (
      <div className="trace-side">
        <div className="trace-side-head is-batch">
          <span className="tsh-kicker">{d.batch.is_semi_finished ? t("Polu-proizvod") : t("Proizvedeni lot")}</span>
          <h3>{d.batch.production_lot_code || d.batch.mix_code || `#${d.batch.batch_id}`}</h3>
          <span className="tsh-sub">{d.batch.product_name || "-"}</span>
        </div>
        <TabBar tabs={tabs} />
        <div className="trace-side-body">
          {activeTab === 0 && (
            <div className="trace-d-list">
              <DRow label={t("Proizvedeni lot")}>{d.batch.production_lot_code || t("— (nije dodijeljen)")}</DRow>
              <DRow label={t("Zamjes (MixCode)")}>{d.batch.mix_code || "-"}</DRow>
              <DRow label={t("Proizvod")}>{d.batch.product_name || "-"}</DRow>
              <DRow label={t("Status")}><StatusPill value={d.batch.batch_status} /></DRow>
              <DRow label={t("Datum proizvodnje")}>{formatDate(d.batch.production_date)}</DRow>
              {d.batch.produced_pieces != null ? <DRow label={t("Proizvedeno")}>{formatPieces(d.batch.produced_pieces)}</DRow> : null}
              {d.batch.packed_pieces != null ? (
                <DRow label={t("Spakirano / odobreno")}>{formatPieces(d.batch.packed_pieces)}</DRow>
              ) : null}
              {d.batch.scrap_pieces != null ? (
                <DRow label={t("Otpis (škart)")}>
                  {`${formatPieces(d.batch.scrap_pieces)}${
                    d.batch.scrap_pct != null ? ` (${Number(d.batch.scrap_pct).toFixed(1).replace(".", ",")} %)` : ""
                  }`}
                </DRow>
              ) : null}
              <DRow label={t("Finalna masa")}>{formatKg(d.batch.final_mass_kg)}</DRow>
              {d.batch.sequence != null ? <DRow label={t("Redoslijed")}>{d.batch.sequence}</DRow> : null}
            </div>
          )}
          {activeTab === 1 && (
            <div className="trace-d-list">
              {d.work_order ? (
                <>
                  <div className="trace-d-subtitle">
                    {t("Radni nalog {code}", { code: d.work_order.code || `#${d.work_order.id}` })}
                  </div>
                  <DRow label={t("Datum naloga")}>{formatDate(d.work_order.production_date)}</DRow>
                  <DRow label={t("Verzija")}>v{d.work_order.version ?? 1}</DRow>
                  <DRow label={t("Status")}><StatusPill value={d.work_order.status} /></DRow>
                  <DRow label={t("Potvrđen")}>{formatDateTime(d.work_order.confirmed_at)}</DRow>
                  {d.work_order.day_order_count ? (
                    <DRow label={t("Toga dana")}>
                      {t("{orders} naloga, {batches} zamjesa", {
                        orders: d.work_order.day_order_count,
                        batches: d.work_order.day_batch_count,
                      })}
                    </DRow>
                  ) : null}
                  {(d.work_order.batches?.length ?? 0) > 1 ? (
                    <>
                      <div className="trace-d-subtitle">
                        {t("Ostalo u istom nalogu ({count})", { count: d.work_order.batches!.length })}
                      </div>
                      <div className="mes-table-wrap">
                        <table className="trace-mini-table">
                          <thead>
                            <tr>
                              <th>{t("Proizvod")}</th>
                              <th style={{ textAlign: "right" }}>{t("Izlaz")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.work_order.batches!.map((b) => (
                              <tr
                                key={b.batch_id}
                                className={b.is_current ? "" : "clickable"}
                                onClick={() => !b.is_current && openNode("batch", b.batch_id)}
                              >
                                <td>
                                  {b.is_semi_finished ? (
                                    <span className="trace-tag">{t("polu-proizvod")}</span>
                                  ) : null}
                                  {b.is_current ? <span className="trace-tag">{t("ovaj")}</span> : null}
                                  {b.product_name || "-"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  {formatQty(b.packed_pieces, b.final_mass_kg)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
                </>
              ) : (
                <div className="trace-empty small">{t("Nije povezan radni nalog")}</div>
              )}
              <div className="trace-d-subtitle">{t("Tijek izrade")}</div>
              {d.work_order?.external_source === "ritam" ? (
                <div className="trace-empty small">
                  {t("Nalog je uvezen iz Ritma, koji ne vodi vremena izrade ni strojeve — ta polja ostaju prazna.")}
                </div>
              ) : null}
              <DRow label={t("Mješalica")}>{p.mixer_name || "-"}</DRow>
              <DRow label={t("Proces / linija")}>{p.process_name || "-"}</DRow>
              <DRow label={t("Peć")}>{p.oven_name || "-"}</DRow>
              {(d.phases || []).length > 0 ? (
                <div className="mes-table-wrap">
                  <table className="trace-mini-table">
                    <thead>
                      <tr>
                        <th>{t("Faza")}</th>
                        <th>{t("Stroj")}</th>
                        <th>{t("Početak")}</th>
                        <th>{t("Kraj")}</th>
                        <th style={{ textAlign: "right" }}>{t("Min")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.phases!.map((ph, i) => (
                        <tr key={`${ph.code || ph.name}-${i}`} title={ph.notes || undefined}>
                          <td>{ph.name}</td>
                          <td>{ph.machine_name || "—"}</td>
                          <td>{ph.start ? formatDateTime(ph.start) : "—"}</td>
                          <td>{ph.end ? formatDateTime(ph.end) : "—"}</td>
                          <td style={{ textAlign: "right" }}>{ph.duration_min != null ? ph.duration_min : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <>
                  <DRow label={t("Miješanje")}>{formatNum(p.mixing_time_min, "min")}</DRow>
                  <DRow label={t("Bulk fermentacija")}>{formatNum(p.bulk_fermentation_min, "min")}</DRow>
                  <DRow label={t("Obrada")}>{formatNum(p.processing_time_min, "min")}</DRow>
                  <DRow label={t("Završno dizanje")}>{formatNum(p.final_proof_min, "min")}</DRow>
                  <DRow label={t("Pečenje")}>{formatNum(p.baking_time_min, "min")}</DRow>
                  {p.mixing_start ? <DRow label={t("Početak miješanja")}>{formatDateTime(p.mixing_start)}</DRow> : null}
                  {p.baking_end ? <DRow label={t("Kraj pečenja")}>{formatDateTime(p.baking_end)}</DRow> : null}
                </>
              )}
              {p.mixing_workers != null || p.processing_workers != null || p.baking_workers != null ? (
                <DRow label={t("Radnici (mij./obr./peč.)")}>
                  {`${p.mixing_workers ?? "-"} / ${p.processing_workers ?? "-"} / ${p.baking_workers ?? "-"}`}
                </DRow>
              ) : null}
            </div>
          )}
          {activeTab === 2 &&
            (d.recipe ? (
              <>
                <div className="trace-d-list">
                  <DRow label={t("Recept")}>{d.recipe.code || `#${d.recipe.id}`}</DRow>
                  <DRow label={t("Verzija")}>v{d.recipe.version ?? 1}</DRow>
                  <DRow label={t("Proizvod")}>{d.recipe.product_name || "-"}</DRow>
                </div>
                <div className="trace-d-subtitle">{t("Sastojci recepture")}</div>
                {d.recipe.ingredients.length === 0 ? (
                  <div className="trace-empty small">{t("Receptura nema definirane sastojke")}</div>
                ) : (
                  <div className="mes-table-wrap">
                    <table className="trace-mini-table">
                    <thead>
                      <tr>
                        <th>{t("Sastojak")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.recipe.ingredients.map((ri, i) => (
                        <tr key={i}>
                          <td>
                            {ri.is_child_recipe || ri.is_semi_finished ? (
                              <span className="trace-tag">{t("polu-proizvod")}</span>
                            ) : null}
                            {ri.name}
                            {ri.is_adjustable ? <span className="trace-tag muted">{t("prilagodljivo")}</span> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            ) : (
              <div className="trace-empty small">{t("Nije povezana receptura")}</div>
            ))}
          {activeTab === 3 &&
            (d.inputs.length === 0 ? (
              <div className="trace-empty small">{t("Nema evidentiranog sastava zamjesa")}</div>
            ) : (
              <div className="mes-table-wrap">
                <table className="trace-mini-table">
                <thead>
                  <tr>
                    <th>{t("Sirovina")}</th>
                    <th>{t("Lot")}</th>
                    <th style={{ textAlign: "right" }}>{t("Količina")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.inputs.map((inp, i) => {
                    const drillBatch = inp.produced_by_batch_id;
                    const clickable = drillBatch ? true : !!inp.lot_id;
                    return (
                      <tr
                        key={i}
                        className={clickable ? "clickable" : ""}
                        onClick={() =>
                          drillBatch
                            ? openNode("batch", drillBatch)
                            : inp.lot_id && openNode("lot", inp.lot_id)
                        }
                      >
                        <td>
                          {inp.is_semi_finished ? <span className="trace-tag">{t("polu-proizvod")}</span> : null}
                          {inp.has_receipt === false && !inp.is_semi_finished ? <span className="trace-tag warn">{t("bez primke")}</span> : null}
                          {inp.is_pooled ? <span className="trace-tag">{t("utrošak naloga")}</span> : null}
                          {inp.ingredient_name || "-"}
                        </td>
                        <td>
                          <strong>{lotBroj(inp.supplier_lot, inp.internal_lot_code)}</strong>
                          {inp.supplier_name ? <span className="td-muted">{inp.supplier_name}</span> : null}
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                          {formatConsumed(inp.ingredient_name, inp.actual_kg ?? inp.planned_kg)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            ))}
          {activeTab === 4 &&
            (d.dispatches.length === 0 ? (
              <div className="trace-empty small">{t("Još nije otpremljeno kupcima")}</div>
            ) : (
              d.dispatches.map((dn, i) => (
                <div
                  className={`trace-doc-block${dn.dispatch_id ? " clickable" : ""}`}
                  key={i}
                  onClick={() => dn.dispatch_id && openNode("dispatch", dn.dispatch_id)}
                >
                  <div className="tdb-head">
                    <span className="tdb-icon">🚚</span>
                    <strong>{dn.customer_name || t("Nepoznat kupac")}</strong>
                  </div>
                  <DRow label={t("Otpremnica")}>{dn.dispatch_number || "-"}</DRow>
                  <DRow label={t("Datum")}>{formatDate(dn.dispatch_date)}</DRow>
                  <DRow label={t("Lot")}>{dn.lot_code || "-"}</DRow>
                  <DRow label={t("Količina")}>{dn.quantity ?? "-"}</DRow>
                </div>
              ))
            ))}
        </div>
      </div>
    );
  };

  const renderDispatchDetail = (d: Extract<TraceNodeDetail, { kind: "dispatch" }>) => {
    const tabs = [t("Otpremnica"), t("Stavke ({count})", { count: d.lines.length })];
    return (
      <div className="trace-side">
        <div className="trace-side-head is-customer">
          <span className="tsh-kicker">{t("Otpremnica")}</span>
          <h3>{d.dispatch.number || `#${d.dispatch.id}`}</h3>
          <span className="tsh-sub">{d.dispatch.customer?.name || t("Nepoznat kupac")}</span>
        </div>
        <TabBar tabs={tabs} />
        <div className="trace-side-body">
          {activeTab === 0 && (
            <div className="trace-d-list">
              <DRow label={t("Broj otpremnice")}>{d.dispatch.number || "-"}</DRow>
              <DRow label={t("Datum")}>{formatDate(d.dispatch.date)}</DRow>
              <DRow label={t("Status")}><StatusPill value={d.dispatch.status} /></DRow>
              <div className="trace-d-subtitle">{t("Kupac")}</div>
              <DRow label={t("Naziv")}>{d.dispatch.customer?.name || "-"}</DRow>
              <DRow label={t("OIB")}>{d.dispatch.customer?.oib ?? "-"}</DRow>
              <DRow label={t("Adresa")}>{d.dispatch.customer?.address || "-"}</DRow>
              {d.dispatch.signed_by ? <DRow label={t("Potpisao")}>{d.dispatch.signed_by}</DRow> : null}
              {d.dispatch.pod_image_url ? (
                <a href={d.dispatch.pod_image_url} target="_blank" rel="noreferrer" className="tdb-link block">
                  {t("Dokaz o dostavi 📎")}
                </a>
              ) : null}
            </div>
          )}
          {activeTab === 1 &&
            (d.lines.length === 0 ? (
              <div className="trace-empty small">{t("Otpremnica nema stavki")}</div>
            ) : (
              <div className="mes-table-wrap">
                <table className="trace-mini-table">
                <thead>
                  <tr>
                    <th>{t("Proizvod")}</th>
                    <th>{t("Lot")}</th>
                    <th style={{ textAlign: "right" }}>{t("Količina")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lines.map((l) => (
                    <tr key={l.id}>
                      <td>{l.product_name || "-"}</td>
                      <td>
                        <strong>{l.lot_code || "-"}</strong>
                        {l.mix_code ? <span className="td-muted">{l.mix_code}</span> : null}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "monospace" }}>{l.quantity ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderReceiptDetail = (d: Extract<TraceNodeDetail, { kind: "receipt" }>) => {
    const tabs = [t("Pregled"), t("Stavke ({count})", { count: d.lines.length })];
    const receiptNote = publicDocNote(d.receipt.notes);
    return (
      <div className="trace-side">
        <div className="trace-side-head is-receipt">
          <span className="tsh-kicker">{t("Primka")}</span>
          <h3>{d.receipt.number || `#${d.receipt.id}`}</h3>
          <span className="tsh-sub">{d.receipt.supplier_name || d.receipt.purchase_order?.supplier_name || d.receipt.warehouse || "-"}</span>
        </div>
        <TabBar tabs={tabs} />
        <div className="trace-side-body">
          {activeTab === 0 && (
            <div className="trace-d-list">
              <DRow label={t("Broj")}>{d.receipt.number || "-"}</DRow>
              <DRow label={t("Datum")}>{formatDate(d.receipt.date)}</DRow>
              <DRow label={t("Dobavljač")}>{d.receipt.supplier_name || d.receipt.purchase_order?.supplier_name || "-"}</DRow>
              <DRow label={t("Skladište")}>
                {d.receipt.warehouse || "-"}
                {d.receipt.warehouse_code ? ` (${d.receipt.warehouse_code})` : ""}
              </DRow>
              <DRow label={t("Narudžbenica")}>{d.receipt.purchase_order?.number || "-"}</DRow>
              {receiptNote ? <DRow label={t("Napomena")}>{receiptNote}</DRow> : null}
              {(d.receipt.image_url || (d.receipt.extra_images && d.receipt.extra_images.length)) ? (
                <DRow label={t("Otpremnica")}>
                  <ReceiptImageThumb
                    images={[
                      ...(d.receipt.image_url ? [{ url: d.receipt.image_url, label: t("Otpremnica") }] : []),
                      ...((d.receipt.extra_images || []).map((img) => ({
                        url: img.url,
                        label: img.label || t("Lot"),
                      }))),
                    ]}
                  />
                </DRow>
              ) : null}
            </div>
          )}
          {activeTab === 1 &&
            (d.lines.length === 0 ? (
              <div className="trace-empty small">{t("Primka nema stavki")}</div>
            ) : (
              <div className="mes-table-wrap">
                <table className="trace-mini-table">
                  <thead>
                    <tr>
                      <th>{t("Sirovina")}</th>
                      <th>{t("Lot")}</th>
                      <th style={{ textAlign: "right" }}>kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lines.map((l) => (
                      <tr key={l.id}>
                        <td>{l.ingredient_name || "-"}</td>
                        <td>
                          <strong>{lotBroj(l.lot_number, l.internal_lot_code)}</strong>
                          {l.expiry_date ? <span className="td-muted">{formatDate(l.expiry_date)}</span> : null}
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>{formatKg(l.quantity_kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderTransferDetail = (d: Extract<TraceNodeDetail, { kind: "transfer" }>) => {
    const tabs = [t("Pregled"), t("Stavke ({count})", { count: d.lines.length })];
    const transferNote = publicDocNote(d.transfer.notes);
    return (
      <div className="trace-side">
        <div className="trace-side-head is-transfer">
          <span className="tsh-kicker">{t("Međuskladišnica")}</span>
          <h3>{d.transfer.number || `#${d.transfer.id}`}</h3>
          <span className="tsh-sub">
            {d.transfer.from_code || d.transfer.from_warehouse || "?"} → {d.transfer.to_code || d.transfer.to_warehouse || "?"}
          </span>
        </div>
        <TabBar tabs={tabs} />
        <div className="trace-side-body">
          {activeTab === 0 && (
            <div className="trace-d-list">
              <DRow label={t("Broj")}>{d.transfer.number || "-"}</DRow>
              <DRow label={t("Datum")}>{formatDate(d.transfer.date)}</DRow>
              <DRow label={t("Status")}>{d.transfer.status || "-"}</DRow>
              <DRow label={t("Od")}>{d.transfer.from_warehouse || d.transfer.from_code || "-"}</DRow>
              <DRow label={t("Na")}>{d.transfer.to_warehouse || d.transfer.to_code || "-"}</DRow>
              {transferNote ? <DRow label={t("Napomena")}>{transferNote}</DRow> : null}
            </div>
          )}
          {activeTab === 1 &&
            (d.lines.length === 0 ? (
              <div className="trace-empty small">{t("Međuskladišnica nema stavki")}</div>
            ) : (
              <div className="mes-table-wrap">
                <table className="trace-mini-table">
                  <thead>
                    <tr>
                      <th>{t("Sirovina")}</th>
                      <th>{t("Lot")}</th>
                      <th style={{ textAlign: "right" }}>kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lines.map((l) => (
                      <tr key={l.id}>
                        <td>{l.ingredient_name || "-"}</td>
                        <td>
                          <strong>{lotBroj(l.lot_number, l.internal_lot_code)}</strong>
                          {l.expiry_date ? <span className="td-muted">{formatDate(l.expiry_date)}</span> : null}
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>{formatKg(l.quantity_kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderQaDetail = (d: Extract<TraceNodeDetail, { kind: "qa" }>) => (
    <div className="trace-side">
      <div className="trace-side-head is-qa">
        <span className="tsh-kicker">{t("Kontrola kvalitete")}</span>
        <h3>{t("QA")}</h3>
        <span className="tsh-sub">
          {d.all_passed === false ? `${t("Ima odbijenih sirovina")} · ` : ""}
          {t("Odobrenje na dan proizvodnje ili ranije")} · {formatDate(d.production_date)}
        </span>
      </div>
      <div className="trace-side-body">
        {d.items.length === 0 ? (
          <div className="trace-empty small">{t("Nema sirovina za QA")}</div>
        ) : (
          <div className="mes-table-wrap">
            <table className="trace-mini-table">
              <thead>
                <tr>
                  <th>{t("Sirovina")}</th>
                  <th>{t("Odobreno")}</th>
                  <th>{t("Izvor")}</th>
                </tr>
              </thead>
              <tbody>
                {d.items.filter((item) => item.ingredient_name).map((item) => (
                  <tr key={item.lot_id}>
                    <td>{item.ingredient_name}</td>
                    <td>{formatDate(item.approved_at)}</td>
                    <td>{qaSourceLabel(item.source)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderCheckForm = (c: FinishedProductCheck) => {
    const weights = c.weight_kg || [];
    const avg =
      weights.filter((w): w is number => w != null).length > 0
        ? weights.filter((w): w is number => w != null).reduce((a, b) => a + b, 0) /
          weights.filter((w): w is number => w != null).length
        : null;
    return (
      <div className="fpqa-form" key={c.id}>
        <div className="trace-d-list">
          <DRow label={t("Datum kontrole")}>{formatDate(c.control_date)}</DRow>
          <DRow label={t("Proizvod / šifra")}>
            {c.product_name || "—"}
            {c.product_code ? ` · ${c.product_code}` : ""}
          </DRow>
          <DRow label={t("Definirana težina")}>{formatPieceWeight(c.defined_weight_kg)}</DRow>
          <DRow label={t("Datum proizvodnje")}>{formatDate(c.production_date)}</DRow>
          <DRow label={t("Odjel proizvodnje")}>{c.production_department || "—"}</DRow>
          <DRow label={t("Miris")}><span className={`fpqa-mark ${c.smell_ok === false ? "fail" : c.smell_ok ? "ok" : ""}`}>{formatSensory(c.smell_ok, c.notes, "miris")}</span></DRow>
          <DRow label={t("Okus")}><span className={`fpqa-mark ${c.taste_ok === false ? "fail" : c.taste_ok ? "ok" : ""}`}>{formatSensory(c.taste_ok, c.notes, "okus")}</span></DRow>
          <DRow label={t("Izgled")}><span className={`fpqa-mark ${c.appearance_ok === false ? "fail" : c.appearance_ok ? "ok" : ""}`}>{formatSensory(c.appearance_ok, c.notes, "izgled")}</span></DRow>
        </div>
        <h4 className="fpqa-sub">{t("Težina")}</h4>
        <div className="mes-table-wrap">
          <table className="trace-mini-table">
            <thead>
              <tr>
                {[1, 2, 3, 4, 5].map((n) => (
                  <th key={n} style={{ textAlign: "right" }}>{n}</th>
                ))}
                <th style={{ textAlign: "right" }}>Ø</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {[0, 1, 2, 3, 4].map((i) => (
                  <td key={i} style={{ textAlign: "right", fontFamily: "monospace" }}>
                    {formatPieceWeight(weights[i])}
                  </td>
                ))}
                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                  {avg != null ? formatPieceWeight(avg) : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <h4 className="fpqa-sub">{t("Volumen proizvoda")}</h4>
        <div className="trace-d-list">
          <DRow label={t("Duljina")}>{c.length_cm != null ? `${c.length_cm} cm` : "—"}</DRow>
          <DRow label={t("Širina")}>{c.width_cm != null ? `${c.width_cm} cm` : "—"}</DRow>
          <DRow label={t("Visina")}>{c.height_cm != null ? `${c.height_cm} cm` : "—"}</DRow>
          <DRow label={t("Zadovoljava")}>
            <span className={`fpqa-mark ${c.conforms === false ? "fail" : c.conforms ? "ok" : ""}`}>{formatMark(c.conforms)}</span>
          </DRow>
          <DRow label={t("Provjera deklaracije")}>
            <span className={`fpqa-mark ${c.label_check_ok === false ? "fail" : c.label_check_ok ? "ok" : ""}`}>{formatMark(c.label_check_ok)}</span>
          </DRow>
          <DRow label={t("Kontrolirao")}>{c.controlled_by || "—"}</DRow>
          <DRow label={t("Nesukladnost / reklamacija")}>{c.nonconformity_number || "—"}</DRow>
          {c.notes ? <DRow label={t("Napomena")}>{c.notes}</DRow> : null}
        </div>
      </div>
    );
  };

  const renderProductQaDetail = (d: Extract<TraceNodeDetail, { kind: "product_qa" }>) => (
    <div className="trace-side">
      <div className="trace-side-head is-product-qa">
        <span className="tsh-kicker">{t("Analiza gotovog proizvoda")}</span>
        <h3>{t("QA")}</h3>
        <span className="tsh-sub">
          {d.product_name || "—"}
          {" · "}
          {d.recorded ? (d.all_passed === false ? t("ne zadovoljava") : t("zadovoljava")) : t("nema unosa")}
        </span>
      </div>
      <div className="trace-side-body">
        <p className="fpqa-note">
          Dnevna kontrola gotovog proizvoda. Degustacija svaki dan u 10:00. Evidenciju vodi tehnolog kontrole kvaliteta.
        </p>
        {d.checks.length === 0 ? (
          <>
            <div className="trace-empty small">{t("Još nema unosa za ovaj lot — polja su iz dnevne kontrole gotovog proizvoda.")}</div>
            {renderCheckForm({
              id: 0,
              product_name: d.product_name,
              production_date: d.production_date,
              weight_kg: [null, null, null, null, null],
            })}
          </>
        ) : (
          d.checks.map((c) => renderCheckForm(c))
        )}
      </div>
    </div>
  );

  const renderDestinationDetail = (d: Extract<TraceNodeDetail, { kind: "destination" }>) => (
    <div className="trace-side">
      <div className="trace-side-head is-destination">
        <span className="tsh-kicker">{d.destination.kind === "wholesale" ? t("Veleprodaja") : d.destination.kind === "production" ? t("Proizvodnja") : t("Skladište")}</span>
        <h3>{d.destination.name || d.destination.code || t("Odredište")}</h3>
        <span className="tsh-sub">{d.destination.code || ""}</span>
      </div>
      <div className="trace-side-body">
        <div className="trace-d-list">
          <DRow label={t("Skladište")}>{d.destination.name || "-"}</DRow>
          <DRow label={t("Šifra")}>{d.destination.code || "-"}</DRow>
          {d.transfer ? (
            <DRow label={t("Međuskladišnica")}>
              {d.transfer.number || `#${d.transfer.id}`} · {formatDate(d.transfer.date)}
            </DRow>
          ) : null}
        </div>
        {d.dispatches.length === 0 ? (
          <div className="trace-empty small">{t("Nema otpremnica na ovo skladište")}</div>
        ) : (
          <div className="mes-table-wrap" style={{ marginTop: "1rem" }}>
            <table className="trace-mini-table">
              <thead>
                <tr>
                  <th>{t("Otpremnica")}</th>
                  <th>{t("Kupac")}</th>
                </tr>
              </thead>
              <tbody>
                {d.dispatches.map((n) => (
                  <tr key={n.dispatch_id ?? n.dispatch_number}>
                    <td>{n.dispatch_number || "-"}</td>
                    <td>{n.customer_name || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // -------------------------------------------------------------- render
  return (
    <div className="mes-page is-traceability">
      <Header title={t("MES - Skladiste")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content trace-page">
          <ToastContainer position="top-center" autoClose={2000} />

          <div className="trace-toolbar">
            <h2>{t("Sljedivost lota")}</h2>
            <div className="trace-toolbar-actions">
              <details className="trace-advanced">
                <summary>{t("Napredno")}</summary>
                <div className="trace-advanced-body">
                  <label className="trace-hidden-toggle">
                    <input
                      type="checkbox"
                      checked={onlyWithReceipt}
                      onChange={(e) => setOnlyWithReceipt(e.target.checked)}
                    />
                    {t("samo s primkom")}
                  </label>
                  <label className="trace-hidden-toggle">
                    <input
                      type="checkbox"
                      checked={includeHidden}
                      onChange={(e) => setIncludeHidden(e.target.checked)}
                    />
                    {t("prikaži skrivene proizvode")}
                  </label>
                </div>
              </details>
              <button
                type="button"
                className="trace-export-btn"
                disabled={viewMode !== "batch-to-lot" || !selectedBatchId || exporting}
                onClick={exportReport}
              >
                {exporting ? t("Pripremam izvještaj…") : t("Izvezi izvještaj")}
              </button>
              <button
                type="button"
                className="trace-export-btn"
                disabled={viewMode !== "batch-to-lot" || !selectedBatchId || docsExporting}
                onClick={exportDocumentation}
              >
                {docsExporting ? t("Pripremam dokumentaciju…") : t("Preuzmi dokumentaciju")}
              </button>
              <div className="trace-toggle">
                <button className={viewMode === "lot-to-batch" ? "active" : ""} onClick={() => switchMode("lot-to-batch")}>
                  {t("Unaprijed · ulazni lot → kupac")}
                </button>
                <button className={viewMode === "batch-to-lot" ? "active" : ""} onClick={() => switchMode("batch-to-lot")}>
                  {t("Unatrag · proizvedeni lot → sirovine")}
                </button>
              </div>
            </div>
          </div>

          {/* ---- search on top ---- */}
          <div className="trace-search-bar" ref={searchRef}>
            <div className="trace-search-input">
              <span className="ts-icon">🔍</span>
              <input
                value={search}
                placeholder={
                  viewMode === "lot-to-batch"
                    ? t("Pretraži ulazni lot po dobavljačevom lotu, sirovini ili dobavljaču…")
                    : t("Pretraži proizvedeni lot po šifri, zamjesu ili proizvodu…")
                }
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
              />
              {hasSelection && (
                <span className="ts-selected" title={selectedLabel}>
                  {selectedLabel}
                </span>
              )}
            </div>
            {searchOpen && (
              <div className="trace-search-results">
                {searchResults.length === 0 ? (
                  <div className="tsr-empty">{t("Nema rezultata")}</div>
                ) : viewMode === "lot-to-batch" ? (
                  (searchResults as TraceMaterialLot[]).map((l) => (
                    <button
                      key={l.lot_id}
                      className={`tsr-item${selectedLotId === l.lot_id ? " active" : ""}`}
                      onClick={() => handleLotSelect(l.lot_id)}
                    >
                      <span className="tsr-code">{lotBroj(l.supplier_lot, l.internal_lot_code) !== "—" ? lotBroj(l.supplier_lot, l.internal_lot_code) : `#${l.lot_id}`}</span>
                      <span className="tsr-main">{l.ingredient_name || "-"}</span>
                      <span className="tsr-sub">
                        {l.supplier_name || ""}
                        {l.usage_count ? ` · ${t("{count} zamjesa", { count: l.usage_count })}` : ""}
                      </span>
                    </button>
                  ))
                ) : (
                  (searchResults as TraceProducedLot[]).map((b) => (
                    <button
                      key={b.batch_id}
                      className={`tsr-item${selectedBatchId === b.batch_id ? " active" : ""}`}
                      onClick={() => handleBatchSelect(b.batch_id)}
                    >
                      <span className="tsr-code">{b.production_lot_code || b.mix_code}</span>
                      <span className="tsr-main">{b.product_name || "-"}</span>
                      <span className="tsr-sub">
                        {b.mix_code || ""}
                        {b.production_date ? ` · ${formatDate(b.production_date)}` : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ---- diagram + sidebar ---- */}
          <div className="trace-workspace">
            <div className="trace-diagram-pane">
              <div className="trace-diagram-head">
                <h3>{t("Dijagram sljedivosti")}</h3>
                <div className="trace-legend">
                  <span><i className="dot-receipt" /> {t("Primka")}</span>
                  <span><i className="dot-qa" /> {t("QA sirovine")}</span>
                  <span><i className="dot-product-qa" /> {t("QA gotov")}</span>
                  <span><i className="dot-transfer" /> {t("Međuskladišnica")}</span>
                  <span><i className="dot-input" /> {t("Ulazni lot")}</span>
                  <span><i className="dot-semi" /> {t("Polu-proizvod")}</span>
                  <span><i className="dot-batch" /> {t("Proizvedeni lot")}</span>
                  <span><i className="dot-dest" /> {t("Odredište")}</span>
                  <span><i className="dot-customer" /> {t("Kupac")}</span>
                </div>
              </div>
              <div className="trace-diagram-scroll">{renderDiagram()}</div>
            </div>

            <aside
              className={`trace-sidebar${selectedNode ? " has-detail" : ""}`}
              ref={(el) => {
                sidebarRef.current = el;
              }}
            >
              {selectedNode ? (
                <button type="button" className="trace-side-close" onClick={resetNode} title={t("Zatvori detalj")}>
                  ×
                </button>
              ) : null}
              {renderSidebar()}
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Traceability;
